'use client'

import type { ElementType, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

interface RevealProps {
    as?: ElementType
    /** Milliseconds. Keep staggers small and cap the total. */
    delay?: number
    className?: string
    children: ReactNode
}

/**
 * Fades and lifts a block into place once, as it enters the viewport.
 * Transform and opacity only, fires once, then stops observing.
 * Reduced motion lands on the same end state instantly (handled in globals.css).
 */
export const Reveal = ({ as: Tag = 'div', delay = 0, className, children }: RevealProps) => {
    const ref = useRef<HTMLElement | null>(null)
    const [shown, setShown] = useState(false)

    useEffect(() => {
        const node = ref.current
        if (!node) return

        if (typeof IntersectionObserver === 'undefined') {
            setShown(true)
            return
        }

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        setShown(true)
                        observer.unobserve(entry.target)
                    }
                }
            },
            { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
        )

        observer.observe(node)
        return () => observer.disconnect()
    }, [])

    return (
        <Tag
            ref={ref}
            style={delay ? { transitionDelay: `${delay}ms` } : undefined}
            className={cn('reveal', shown && 'revealed', className)}
        >
            {children}
        </Tag>
    )
}

export default Reveal
